import type { Scene } from '@lumen5/luminary';

/**
 * Reads a scene's raw scene-design config — the template's own defaults for every asset, before
 * anything the author changed. Returns null when the config can't be read (a Luminary without the
 * private accessor, or a design that fails to load) so callers skip the comparison rather than
 * crash.
 */
function getSceneDesignConfig(scene: Scene): unknown {
    try {
        const root = scene.root as unknown as {
            _getSceneDesignConfig?: (sceneDesignId: string, aspectRatio: string) => unknown;
            aspectRatio: string;
        };
        if (typeof root._getSceneDesignConfig !== 'function') {
            return null;
        }
        return root._getSceneDesignConfig(scene.sceneDesign, root.aspectRatio);
    } catch {
        return null;
    }
}

/**
 * Walks a scene-design config collecting one value per named asset. The config is an opaque nested
 * structure (asset groups, subslides, etc.), so every object is visited looking for the shape the
 * extractor recognizes rather than assuming a fixed tree layout. `extract` returns undefined for
 * nodes it doesn't care about.
 */
function collectByAssetName<T>(
    node: unknown,
    extract: (node: Record<string, unknown>) => T | undefined,
    map: Map<string, T>,
): void {
    if (!node || typeof node !== 'object') {
        return;
    }
    if (Array.isArray(node)) {
        node.forEach((child) => collectByAssetName(child, extract, map));
        return;
    }
    const obj = node as Record<string, unknown>;
    if (typeof obj.name === 'string') {
        const value = extract(obj);
        if (value !== undefined) {
            map.set(obj.name, value);
        }
    }
    Object.values(obj).forEach((value) => {
        if (value && typeof value === 'object') {
            collectByAssetName(value, extract, map);
        }
    });
}

/**
 * Map of asset name -> the scene design's default value for it, for whichever assets the extractor
 * recognizes. Returns null when the design config can't be read.
 */
export function getSceneDesignDefaults<T>(
    scene: Scene,
    extract: (node: Record<string, unknown>) => T | undefined,
): Map<string, T> | null {
    const config = getSceneDesignConfig(scene);
    if (config === null) {
        return null;
    }
    const map = new Map<string, T>();
    collectByAssetName(config, extract, map);
    return map;
}

/** Asset name -> the scene design's default text, for every named asset carrying text. */
export function getSceneDesignDefaultText(scene: Scene): Map<string, string> | null {
    return getSceneDesignDefaults(scene, (node) =>
        typeof node.text === 'string' ? node.text : undefined,
    );
}

/**
 * Asset name -> default text, limited to the slots an author can actually fill. Decorative text
 * baked into a design — the oversized quotation glyph on a quote layout, for instance — ships as
 * a named text asset with `isEditable: false`, so it has to be excluded when counting how many
 * slots a design expects the author to populate.
 */
export function getSceneDesignEditableDefaultText(scene: Scene): Map<string, string> | null {
    return getSceneDesignDefaults(scene, (node) =>
        typeof node.text === 'string' && node.isEditable !== false ? node.text : undefined,
    );
}

/** Asset name -> the scene design's default icon url, for every named asset carrying one. */
export function getSceneDesignDefaultIconUrls(scene: Scene): Map<string, string> | null {
    return getSceneDesignDefaults(scene, (node) => {
        const sourceData = node.sourceData as Record<string, unknown> | undefined;
        return sourceData && typeof sourceData.url === 'string' ? sourceData.url : undefined;
    });
}

/**
 * The slot index in a scene design's color reference (`themeRef_colors_1` -> 1). Undefined for a
 * literal color or anything unrecognized, so callers skip the comparison rather than guess.
 */
function parseColorSlot(fill: unknown): number | undefined {
    if (typeof fill !== 'string') {
        return undefined;
    }
    const match = /^themeRef_colors_(\d+)$/.exec(fill);
    return match ? Number(match[1]) : undefined;
}

/**
 * The slot index a serialized color carries (`"#ffffff (White) [slot 0]"` -> 0). Undefined when the
 * value has no slot suffix — an asset on a literal color, which is not comparable to a
 * slot-referenced default.
 */
function parseSerializedColorSlot(colors: string | undefined): number | undefined {
    const match = colors ? /\[slot (\d+)\]/.exec(colors) : null;
    return match ? Number(match[1]) : undefined;
}

/** The styling properties a scene design supplies as defaults for a named asset. */
export interface SceneDesignStyling {
    textSize?: number;
    /**
     * The template color slot the design points the asset's text at. Real scene designs express
     * color as a slot reference (`themeRef_colors_1`) rather than a literal, so the slot index is
     * the only form of the default that is comparable without resolving the template's palette.
     */
    colorSlot?: number;
}

/**
 * Asset name -> the scene design's default styling for it (text size and fill color), for every
 * named asset the design styles.
 *
 * This is what makes an authored value distinguishable from an inherited one: a serialized asset
 * shows its effective size and color either way, so only a comparison against the design's own
 * default reveals which of them somebody actually chose.
 */
export function getSceneDesignDefaultStyling(scene: Scene): Map<string, SceneDesignStyling> | null {
    return getSceneDesignDefaults(scene, (node) => {
        const styling: SceneDesignStyling = {};
        if (typeof node.textSize === 'number') {
            styling.textSize = node.textSize;
        }
        const normalStyle = node.normalStyle as Record<string, unknown> | undefined;
        const colorSlot = parseColorSlot(normalStyle?.fill);
        if (colorSlot !== undefined) {
            styling.colorSlot = colorSlot;
        }
        return Object.keys(styling).length > 0 ? styling : undefined;
    });
}

/**
 * The subset of an asset's serialized styling that differs from its scene design's default, keyed
 * by the serialized property name so the formatter can mark those lines.
 *
 * Returns an empty object when the design's defaults can't be read — an unreadable design means
 * "nothing known to be authored", never "everything was authored".
 *
 * Note on `fontSize`: text is auto-fitted to its box on edit (`resizeTextToFitOnScene`), so a size
 * that differs from the default can reflect a longer or shorter line rather than a deliberate
 * choice. It is still reported — the size a reference actually renders at is worth reproducing
 * either way — but it is the one marker that does not always mean "somebody chose this".
 */
export function getChangedFromDesign(
    defaults: Map<string, SceneDesignStyling> | null,
    assetName: string,
    serialized: { fontSize?: number; colors?: string },
): Record<string, unknown> {
    const assetDefault = defaults?.get(assetName);
    if (!assetDefault) {
        return {};
    }

    const changed: Record<string, unknown> = {};
    if (
        assetDefault.textSize !== undefined &&
        serialized.fontSize !== undefined &&
        Math.round(assetDefault.textSize) !== Math.round(serialized.fontSize)
    ) {
        changed.fontSize = Math.round(assetDefault.textSize);
    }
    // Both sides as a slot index: the design's default is a slot reference, and the serialized
    // color carries its slot as a `[slot N]` suffix. Comparing slots avoids resolving either to a
    // literal, so an asset that simply inherits its design's slot never reads as a change even
    // though the template's palette decides what that slot looks like.
    const serializedSlot = parseSerializedColorSlot(serialized.colors);
    if (
        assetDefault.colorSlot !== undefined &&
        serializedSlot !== undefined &&
        serializedSlot !== assetDefault.colorSlot
    ) {
        changed.colors = `slot ${assetDefault.colorSlot}`;
    }
    return changed;
}
